import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api, { fmt, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, Plus, Minus, Trash2, Zap, Printer, BadgePercent } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export default function POS({ variant = "standard" }) {
  const { user } = useAuth();
  const isPd = variant === "pd";
  const [catalog, setCatalog] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [cart, setCart] = useState([]);
  const [customer, setCustomer] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [mechanicId, setMechanicId] = useState("");
  const [discount, setDiscount] = useState(isPd ? 15 : 0);
  const [employeeDisc, setEmployeeDisc] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/catalog").then((r) => setCatalog(r.data));
    if (user.role === "admin") api.get("/mechanics").then((r) => setMechanics(r.data));
  }, [user.role]);

  const categories = useMemo(
    () => ["All", ...new Set(catalog.map((c) => c.category))],
    [catalog]
  );

  const filtered = catalog.filter(
    (c) =>
      (category === "All" || c.category === category) &&
      c.name.toLowerCase().includes(query.toLowerCase())
  );

  const assignedRate = useMemo(() => {
    if (user.role === "admin" && mechanicId) {
      const m = mechanics.find((x) => x.id === mechanicId);
      return m ? m.commission_rate : 0;
    }
    return user.commission_rate ?? 25;
  }, [user, mechanicId, mechanics]);

  const priceOf = (i) => (employeeDisc && i.cost != null ? i.cost : i.price);
  const subtotal = cart.reduce((s, i) => s + priceOf(i) * i.qty, 0);
  const discountAmt = subtotal * (discount / 100);
  const total = subtotal - discountAmt;
  const commission = employeeDisc ? 0 : total * (assignedRate / 100);

  const add = (p) => {
    setCart((prev) => {
      const ex = prev.find((i) => i.id === p.id);
      if (ex) return prev.map((i) => (i.id === p.id ? { ...i, qty: i.qty + 1 } : i));
      return [...prev, { ...p, qty: 1 }];
    });
  };
  const dec = (id) =>
    setCart((prev) =>
      prev.map((i) => (i.id === id ? { ...i, qty: i.qty - 1 } : i)).filter((i) => i.qty > 0)
    );
  const remove = (id) => setCart((prev) => prev.filter((i) => i.id !== id));

  const recordSale = async () => {
    if (!customer.trim()) return toast.error("Enter the customer name / citizen ID");
    if (!cart.length) return toast.error("Cart is empty");
    setBusy(true);
    try {
      const { data } = await api.post("/sales", {
        customer: customer.trim(),
        vehicle: vehicle.trim(),
        mechanic_id: user.role === "admin" ? mechanicId || undefined : undefined,
        items: cart.map((i) => ({ product_id: i.id, qty: i.qty })),
        discount_percent: Number(discount) || 0,
        sale_type: isPd ? "pd_ems" : "standard",
        employee_discount: employeeDisc,
      });
      setReceipt(data);
      setCart([]);
      setCustomer("");
      setVehicle("");
      setDiscount(isPd ? 15 : 0);
      setEmployeeDisc(false);
      toast.success("Sale recorded — commission logged");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <div data-testid="pos-page">
      <div className="mb-6">
        <div className="text-[10px] tracking-[0.4em] text-primary mb-2 flex items-center gap-3">
          <span className="h-px w-8 bg-primary/60" />
          {isPd ? "PD & EMS POS — 15% OFF EVERYTHING" : "POINT OF SALE"}{" "}
          <span className="font-jp text-muted-foreground">{isPd ? "警察・救急" : "販売時点情報管理"}</span>
        </div>
        <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tight uppercase">
          {isPd ? "PD & EMS Sale" : "Build a Sale"}
        </h1>
      </div>

      {isPd && (
        <div className="mb-4 border border-sky-400/40 bg-sky-400/10 rounded-sm px-4 py-3 flex items-center gap-3" data-testid="pd-ems-banner">
          <span className="text-sky-400 font-display text-[11px] tracking-[0.25em] font-bold">PD & EMS PRICING ACTIVE</span>
          <span className="text-[11px] text-sky-200/70">Every part and service is automatically 15% off — no manual discount needed.</span>
        </div>
      )}

      <div className="bg-card border border-border rounded-md p-4 mb-4 grid sm:grid-cols-3 gap-3" data-testid="pos-customer-bar">
        <Input
          data-testid="pos-customer-input"
          placeholder="Customer name / Citizen ID"
          value={customer}
          onChange={(e) => setCustomer(e.target.value)}
          className="bg-secondary/60 border-input rounded-sm h-11 text-sm"
        />
        <Input
          data-testid="pos-vehicle-input"
          placeholder="Vehicle & plate (optional)"
          value={vehicle}
          onChange={(e) => setVehicle(e.target.value)}
          className="bg-secondary/60 border-input rounded-sm h-11 text-sm"
        />
        {user.role === "admin" ? (
          <select
            data-testid="pos-mechanic-select"
            value={mechanicId}
            onChange={(e) => setMechanicId(e.target.value)}
            className="bg-secondary/60 border border-input rounded-sm h-11 px-3 text-sm text-foreground"
          >
            <option value="">Assign mechanic… (you)</option>
            {mechanics.filter((m) => m.active !== false).map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} — {m.commission_rate}%
              </option>
            ))}
          </select>
        ) : (
          <div className="flex items-center px-3 h-11 border border-input rounded-sm bg-secondary/40 text-xs text-muted-foreground" data-testid="pos-mechanic-self">
            Mechanic: <span className="text-primary ml-1.5 font-semibold">{user.name}</span>
            <span className="ml-auto text-primary">{user.commission_rate}%</span>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-12 gap-4">
        <div className="lg:col-span-7">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="relative flex-1 min-w-[180px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="pos-search-input"
                placeholder="Search parts & services…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-9 bg-card border-border rounded-sm h-10 text-sm"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 mb-4" data-testid="pos-category-pills">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`pos-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setCategory(c)}
                className={`text-[10px] tracking-widest px-3 py-1.5 rounded-sm border transition-colors ${
                  category === c
                    ? "bg-primary text-primary-foreground border-primary font-bold"
                    : "border-border text-muted-foreground hover:text-foreground hover:border-primary/40"
                }`}
              >
                {c.toUpperCase()}
              </button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-2.5 max-h-[560px] overflow-y-auto pr-1" data-testid="pos-catalog-grid">
            {filtered.map((p, i) => (
              <motion.button
                key={p.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, delay: Math.min(i * 0.02, 0.4) }}
                onClick={() => add(p)}
                data-testid={`pos-product-${p.id}`}
                className="text-left bg-card border border-border rounded-sm p-3.5 card-glow transition-all duration-200 hover:-translate-y-0.5 group"
              >
                <div className="text-[9px] tracking-[0.2em] text-muted-foreground mb-1.5">{p.category.toUpperCase()}</div>
                <div className="text-xs font-semibold text-foreground leading-snug min-h-[2rem]">{p.name}</div>
                <div className="flex items-center justify-between mt-2.5">
                  <span className="font-display text-sm font-bold text-primary">{fmt(p.price)}</span>
                  <span className="w-6 h-6 rounded-sm border border-primary/40 text-primary flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Plus size={12} />
                  </span>
                </div>
              </motion.button>
            ))}
            {!filtered.length && (
              <div className="col-span-full text-center text-muted-foreground text-xs py-10" data-testid="pos-catalog-empty">
                Nothing matches that search.
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="bg-card border border-primary/25 rounded-md p-5 sticky top-6 shadow-[0_0_30px_rgba(0,255,102,0.06)]" data-testid="pos-cart">
            <div className="flex items-center justify-between mb-4">
              <span className="text-[10px] tracking-[0.25em] text-muted-foreground">ORDER TERMINAL</span>
              <span className="font-jp text-[10px] text-primary/60">注文</span>
            </div>
            <div className="space-y-2 max-h-[260px] overflow-y-auto pr-1 mb-4">
              <AnimatePresence initial={false}>
                {cart.map((i) => (
                  <motion.div
                    key={i.id}
                    layout
                    initial={{ opacity: 0, x: 16 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -16 }}
                    className="flex items-center gap-2 text-xs bg-secondary/50 border border-border rounded-sm px-3 py-2"
                    data-testid={`cart-item-${i.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-foreground font-semibold truncate">{i.name}</div>
                      <div className="text-muted-foreground text-[10px]">
                        {fmt(priceOf(i))} ea
                        {employeeDisc && i.cost != null && <span className="text-primary ml-1.5 line-through opacity-60">{fmt(i.price)}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button data-testid={`cart-dec-${i.id}`} onClick={() => dec(i.id)} className="w-5 h-5 border border-border rounded-sm flex items-center justify-center text-muted-foreground hover:text-primary">
                        <Minus size={10} />
                      </button>
                      <span className="w-5 text-center text-foreground">{i.qty}</span>
                      <button data-testid={`cart-inc-${i.id}`} onClick={() => add(i)} className="w-5 h-5 border border-border rounded-sm flex items-center justify-center text-muted-foreground hover:text-primary">
                        <Plus size={10} />
                      </button>
                    </div>
                    <span className="w-16 text-right text-foreground font-semibold">{fmt(priceOf(i) * i.qty)}</span>
                    <button data-testid={`cart-remove-${i.id}`} onClick={() => remove(i.id)} className="text-muted-foreground hover:text-destructive">
                      <Trash2 size={12} />
                    </button>
                  </motion.div>
                ))}
              </AnimatePresence>
              {!cart.length && (
                <div className="text-muted-foreground text-xs text-center py-8" data-testid="cart-empty">
                  Cart is empty — tap parts to add them.
                </div>
              )}
            </div>

            <div className={`flex items-center justify-between gap-3 mb-4 border rounded-sm px-3 py-2.5 transition-colors ${employeeDisc ? "border-primary/50 bg-primary/10" : "border-border bg-secondary/40"}`} data-testid="employee-discount-row">
              <span className="flex items-center gap-2 min-w-0">
                <BadgePercent size={14} className={employeeDisc ? "text-primary" : "text-muted-foreground"} />
                <span>
                  <span className="block text-[10px] tracking-[0.2em] text-foreground font-semibold">EMPLOYEE DISCOUNT</span>
                  <span className="block text-[10px] text-muted-foreground">charges shop cost — no commission</span>
                </span>
              </span>
              <Switch
                data-testid="employee-discount-switch"
                checked={employeeDisc}
                onCheckedChange={setEmployeeDisc}
              />
            </div>

            <div className="flex items-center gap-3 mb-4">
              <span className="text-[10px] tracking-[0.2em] text-muted-foreground">DISCOUNT %</span>
              {isPd ? (
                <span className="text-[10px] tracking-widest text-sky-400 border border-sky-400/40 bg-sky-400/10 px-3 py-2 rounded-sm font-bold" data-testid="pd-discount-badge">
                  15% — PD & EMS AUTO
                </span>
              ) : (
                <Input
                  data-testid="pos-discount-input"
                  type="number"
                  min="0"
                  max="100"
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                  className="w-20 h-8 bg-secondary/60 border-input rounded-sm text-xs"
                />
              )}
            </div>

            <div className="border-t border-border pt-3 space-y-1.5 text-xs">
              <div className="flex justify-between text-muted-foreground">
                <span>Subtotal</span>
                <span data-testid="cart-subtotal">{fmt(subtotal)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-amber-400">
                  <span>Discount ({discount}%)</span>
                  <span data-testid="cart-discount">−{fmt(discountAmt)}</span>
                </div>
              )}
              <div className="flex justify-between items-center pt-1.5">
                <span className="text-[10px] tracking-[0.25em] text-muted-foreground">CUSTOMER TOTAL</span>
                <span className="font-display text-2xl font-black text-foreground" data-testid="cart-total">{fmt(total)}</span>
              </div>
              <div className="flex justify-between items-center bg-primary/10 border border-primary/30 rounded-sm px-3 py-2 mt-1">
                <span className="text-[10px] tracking-[0.2em] text-primary">COMMISSION ({assignedRate}%)</span>
                <span className="font-display text-sm font-bold text-primary" data-testid="cart-commission">
                  {employeeDisc ? "—" : `+${fmt(commission)}`}
                </span>
              </div>
            </div>

            <Button
              data-testid="pos-record-sale-btn"
              onClick={recordSale}
              disabled={busy || !cart.length}
              className="w-full mt-4 bg-primary text-primary-foreground hover:bg-primary/90 font-display text-[11px] tracking-[0.2em] h-12 rounded-sm"
            >
              <Zap size={14} className="mr-2" />
              {busy ? "RECORDING…" : "RECORD SALE & LOG COMMISSION"}
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={!!receipt} onOpenChange={() => setReceipt(null)}>
        <DialogContent className="bg-[#0C100D] border-primary/30 text-foreground max-w-md" data-testid="receipt-modal">
          {receipt && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight flex items-center gap-2">
                  <Printer size={16} className="text-primary" /> TOP GEAR RECEIPT
                </DialogTitle>
              </DialogHeader>
              <div className="text-xs space-y-3 font-mono">
                <div className="flex justify-between text-muted-foreground border-b border-dashed border-border pb-2">
                  <span>{receipt.customer}</span>
                  <span>{new Date(receipt.created_at).toLocaleString()}</span>
                </div>
                {receipt.vehicle && <div className="text-muted-foreground">{receipt.vehicle}</div>}
                <div className="space-y-1.5">
                  {receipt.items.map((i, idx) => (
                    <div key={idx} className="flex justify-between">
                      <span className="text-foreground">
                        {i.qty}× {i.name}
                      </span>
                      <span>{fmt(i.line_total)}</span>
                    </div>
                  ))}
                </div>
                {receipt.employee_pricing && (
                  <div className="flex justify-between text-primary border border-primary/30 bg-primary/5 rounded-sm px-2 py-1.5 text-[10px] tracking-widest" data-testid="receipt-employee-pricing">
                    <span>EMPLOYEE PRICING</span>
                    <span>SHOP COST APPLIED</span>
                  </div>
                )}
                {receipt.discount_amount > 0 && (
                  <div className="flex justify-between text-amber-400 border-t border-dashed border-border pt-2">
                    <span>{receipt.sale_type === "pd_ems" ? "PD & EMS Discount" : "Discount"} ({receipt.discount_percent}%)</span>
                    <span>−{fmt(receipt.discount_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between font-display font-black text-base text-foreground border-t border-dashed border-border pt-2">
                  <span>TOTAL</span>
                  <span data-testid="receipt-total">{fmt(receipt.total)}</span>
                </div>
                <div className="flex justify-between text-primary border border-primary/30 bg-primary/10 rounded-sm px-3 py-2">
                  <span>{receipt.mechanic_name} commission ({receipt.commission_rate}%)</span>
                  <span data-testid="receipt-commission">+{fmt(receipt.commission)}</span>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
